import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ClientDto, ClientInput } from '@jordan/types';
import {
  Alert,
  Avatar,
  Breadcrumb,
  Button,
  ClientCard,
  DataTable,
  type DataTableColumn,
  ImageUpload,
  Input,
  Modal,
  PageHeader,
  Spinner,
  Textarea,
} from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { apiDelete, apiGet, apiPost, apiPut, assetUrl } from '../../lib/api';
import { billingTimeSidebar } from './billingTimeSidebar';

// Editable string form (rate kept as a string for the number input).
interface ClientForm {
  name: string;
  company: string;
  email: string;
  phone: string;
  website: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  defaultRate: string;
  currency: string;
  taxId: string;
  billingEmail: string;
  notes: string;
}

const EMPTY: ClientForm = {
  name: '',
  company: '',
  email: '',
  phone: '',
  website: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  region: '',
  postalCode: '',
  country: '',
  defaultRate: '',
  currency: 'USD',
  taxId: '',
  billingEmail: '',
  notes: '',
};

const toForm = (c: ClientDto): ClientForm => ({
  name: c.name,
  company: c.company ?? '',
  email: c.email ?? '',
  phone: c.phone ?? '',
  website: c.website ?? '',
  addressLine1: c.addressLine1 ?? '',
  addressLine2: c.addressLine2 ?? '',
  city: c.city ?? '',
  region: c.region ?? '',
  postalCode: c.postalCode ?? '',
  country: c.country ?? '',
  defaultRate: c.defaultRate == null ? '' : String(c.defaultRate),
  currency: c.currency || 'USD',
  taxId: c.taxId ?? '',
  billingEmail: c.billingEmail ?? '',
  notes: c.notes ?? '',
});

function formatRate(c: ClientDto): string {
  if (c.defaultRate == null) return '—';
  try {
    return `${new Intl.NumberFormat(undefined, { style: 'currency', currency: c.currency || 'USD' }).format(c.defaultRate)}/hr`;
  } catch {
    return `${c.defaultRate}/hr`;
  }
}

export function ClientsPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [clients, setClients] = useState<ClientDto[] | null>(null);
  const [view, setView] = useState<'card' | 'table'>('card');
  const [err, setErr] = useState<string | null>(null);

  // Modal state: editing a client (id), creating ('new'), or closed (null).
  const [editing, setEditing] = useState<ClientDto | 'new' | null>(null);
  const [form, setForm] = useState<ClientForm>(EMPTY);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  // undefined = unchanged, null = cleared, string = new data URL.
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const load = () =>
    apiGet<ClientDto[]>('/api/consulting/clients')
      .then(setClients)
      .catch(() => setErr('Could not load clients.'));
  useEffect(() => {
    void load();
  }, []);

  const openNew = () => {
    setEditing('new');
    setForm(EMPTY);
    setPhotoPreview(null);
    setPhotoDataUrl(undefined);
  };
  const openEdit = (c: ClientDto) => {
    setEditing(c);
    setForm(toForm(c));
    setPhotoPreview(assetUrl(c.photoUrl) ?? null);
    setPhotoDataUrl(undefined);
  };
  const close = () => setEditing(null);

  const set = (key: keyof ClientForm, value: string) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    if (!form.name.trim()) {
      setErr('A client name is required.');
      return;
    }
    setBusy(true);
    setErr(null);
    const payload: ClientInput = {
      name: form.name.trim(),
      company: form.company,
      email: form.email,
      phone: form.phone,
      website: form.website,
      addressLine1: form.addressLine1,
      addressLine2: form.addressLine2,
      city: form.city,
      region: form.region,
      postalCode: form.postalCode,
      country: form.country,
      defaultRate: form.defaultRate.trim() === '' ? null : Number(form.defaultRate),
      currency: form.currency.trim() || 'USD',
      taxId: form.taxId,
      billingEmail: form.billingEmail,
      notes: form.notes,
      ...(photoDataUrl === undefined ? {} : { photoDataUrl }),
    };
    try {
      if (editing === 'new') await apiPost('/api/consulting/clients', payload);
      else if (editing) await apiPut(`/api/consulting/clients/${editing.id}`, payload);
      close();
      await load();
    } catch {
      setErr('Could not save the client.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c: ClientDto) => {
    if (!window.confirm(`Delete ${c.name}? This can’t be undone.`)) return;
    try {
      await apiDelete(`/api/consulting/clients/${c.id}`);
      await load();
    } catch {
      setErr('Could not delete the client.');
    }
  };

  const columns: DataTableColumn<ClientDto>[] = [
      {
        key: 'name',
        header: 'Client',
        sortable: true,
        sortValue: (c) => c.name.toLowerCase(),
        render: (c) => (
          <div className="d-flex align-items-center">
            <Avatar size={32} initials={c.name.slice(0, 2)} src={assetUrl(c.photoUrl)} />
            <div className="ms-2">
              <div className="fw-semibold">{c.name}</div>
              {c.company && <div className="text-muted fs-sm">{c.company}</div>}
            </div>
          </div>
        ),
      },
      { key: 'email', header: 'Email', sortable: true, render: (c) => c.email || '—' },
      { key: 'phone', header: 'Phone', render: (c) => c.phone || '—' },
      {
        key: 'defaultRate',
        header: 'Default rate',
        align: 'end',
        sortable: true,
        sortValue: (c) => c.defaultRate ?? -1,
        render: (c) => formatRate(c),
      },
      {
        key: 'actions',
        header: '',
        align: 'end',
        render: (c) => (
          <div className="d-flex justify-content-end gap-1">
            <button className="btn btn-light btn-icon btn-sm rounded-pill border-transparent" title="Edit" onClick={() => openEdit(c)}>
              <i className="ph-pencil" />
            </button>
            <button
              className="btn btn-light btn-icon btn-sm rounded-pill border-transparent text-danger"
              title="Delete"
              onClick={() => void remove(c)}
            >
              <i className="ph-trash" />
            </button>
          </div>
        ),
      },
  ];

  return (
    <AppChrome secondarySidebar={billingTimeSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Billing &amp; Time - <span className="fw-normal">Clients</span>
          </>
        }
        breadcrumb={
          <Breadcrumb
            items={[
              { icon: 'ph-house', href: '/' },
              { label: 'Consulting' },
              { label: 'Billing & Time' },
              { label: 'Clients' },
            ]}
          />
        }
        actions={
          <Button variant="primary" icon="ph-plus" onClick={openNew}>
            Add client
          </Button>
        }
      />

      <div className="content">
        {err && (
          <Alert variant="warning" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        <div className="d-flex align-items-center mb-3">
          <span className="text-muted">{clients ? `${clients.length} client${clients.length === 1 ? '' : 's'}` : ''}</span>
          <div className="btn-group ms-auto">
            <button className={`btn btn-sm ${view === 'card' ? 'btn-primary' : 'btn-light'}`} onClick={() => setView('card')}>
              <i className="ph-squares-four me-1" /> Cards
            </button>
            <button className={`btn btn-sm ${view === 'table' ? 'btn-primary' : 'btn-light'}`} onClick={() => setView('table')}>
              <i className="ph-table me-1" /> Table
            </button>
          </div>
        </div>

        {clients === null ? (
          <Spinner />
        ) : clients.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <i className="ph-users d-block fs-1 text-muted mb-2" />
              <h5 className="mb-1">No clients yet</h5>
              <p className="text-muted mb-3">Add your first client to start tracking projects and billing.</p>
              <Button variant="primary" icon="ph-plus" onClick={openNew}>
                Add client
              </Button>
            </div>
          </div>
        ) : view === 'card' ? (
          <div className="row g-3">
            {clients.map((c) => (
              <div className="col-xl-3 col-md-4 col-sm-6" key={c.id}>
                <ClientCard
                  name={c.name}
                  company={c.company}
                  imageSrc={assetUrl(c.photoUrl)}
                  rate={formatRate(c)}
                  email={c.email}
                  phone={c.phone}
                  website={c.website}
                  onEdit={() => openEdit(c)}
                  onDelete={() => void remove(c)}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="card border-0 shadow-sm">
            <DataTable
              columns={columns}
              rows={clients}
              getRowKey={(c) => c.id}
              searchable
              searchPlaceholder="Search clients…"
              initialSort={{ key: 'name', dir: 'asc' }}
              pageSize={15}
            />
          </div>
        )}
      </div>

      {editing && (
        <Modal
          open
          size="lg"
          onClose={close}
          title={editing === 'new' ? 'Add client' : `Edit ${(editing as ClientDto).name}`}
          footer={
            <>
              <Button variant="light" onClick={close}>
                Cancel
              </Button>
              <Button variant="primary" disabled={busy} onClick={() => void save()}>
                {editing === 'new' ? 'Create client' : 'Save changes'}
              </Button>
            </>
          }
        >
          <div className="mb-3">
            <ImageUpload
              previewSrc={photoPreview}
              previewSize={96}
              hint="Square image works best for the card."
              onSelect={(_f, dataUrl) => {
                setPhotoDataUrl(dataUrl);
                setPhotoPreview(dataUrl);
              }}
              onRemove={() => {
                setPhotoDataUrl(null);
                setPhotoPreview(null);
              }}
            />
          </div>

          <h6 className="text-muted text-uppercase fs-sm">Details</h6>
          <div className="row">
            <div className="col-md-6">
              <Input label="Name" value={form.name} onChange={(e) => set('name', e.target.value)} />
            </div>
            <div className="col-md-6">
              <Input label="Company" value={form.company} onChange={(e) => set('company', e.target.value)} />
            </div>
            <div className="col-md-6">
              <Input label="Email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
            </div>
            <div className="col-md-6">
              <Input label="Phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </div>
            <div className="col-12">
              <Input label="Website" value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://" />
            </div>
          </div>

          <h6 className="text-muted text-uppercase fs-sm mt-2">Address</h6>
          <div className="row">
            <div className="col-12">
              <Input label="Address line 1" value={form.addressLine1} onChange={(e) => set('addressLine1', e.target.value)} />
            </div>
            <div className="col-12">
              <Input label="Address line 2" value={form.addressLine2} onChange={(e) => set('addressLine2', e.target.value)} />
            </div>
            <div className="col-md-5">
              <Input label="City" value={form.city} onChange={(e) => set('city', e.target.value)} />
            </div>
            <div className="col-md-3">
              <Input label="State / region" value={form.region} onChange={(e) => set('region', e.target.value)} />
            </div>
            <div className="col-md-4">
              <Input label="Postal code" value={form.postalCode} onChange={(e) => set('postalCode', e.target.value)} />
            </div>
            <div className="col-12">
              <Input label="Country" value={form.country} onChange={(e) => set('country', e.target.value)} />
            </div>
          </div>

          <h6 className="text-muted text-uppercase fs-sm mt-2">Billing</h6>
          <div className="row">
            <div className="col-md-4">
              <Input
                label="Default rate (per hour)"
                type="number"
                min={0}
                step="0.01"
                value={form.defaultRate}
                onChange={(e) => set('defaultRate', e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <Input label="Currency" value={form.currency} onChange={(e) => set('currency', e.target.value)} />
            </div>
            <div className="col-md-6">
              <Input label="Billing email" type="email" value={form.billingEmail} onChange={(e) => set('billingEmail', e.target.value)} />
            </div>
            <div className="col-12">
              <Input label="Tax ID" value={form.taxId} onChange={(e) => set('taxId', e.target.value)} />
            </div>
            <div className="col-12">
              <Textarea
                label="Notes"
                rows={3}
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
              />
            </div>
          </div>
          <div className="text-muted fs-sm">
            The default rate is used until a project sets its own — a project’s rate is what bills.
          </div>
        </Modal>
      )}
    </AppChrome>
  );
}

